import {
  Stack,
  Button,
  Group,
  Divider,
  Text,
  Anchor,
  Image,
  Tooltip,
  Switch,
  SegmentedControl,
  CloseButton,
  Box,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useAppStore } from "../../store/appStore";
import type { LayerType } from "../../types";
import { HindcastSelector } from "./HindcastSelector";
import { LocationInputs } from "./LocationInputs";
import { RadiusSlider } from "./RadiusSlider";
import { RecentValidations } from "./RecentValidations";

const MODE_TOOLTIP =
  "Select the variable to display on the map and validate: wave height (Hs) or wind speed.";

function SectionLabel({ children }: { children: string }) {
  return (
    <Text
      size="xs"
      tt="uppercase"
      fw={700}
      c="oceanumPrimary.4"
      style={{ letterSpacing: "0.09em" }}
    >
      {children}
    </Text>
  );
}

interface SidebarProps {
  onClose?: () => void;
}

export function Sidebar({ onClose }: SidebarProps) {
  const {
    selectedHindcast,
    lon,
    lat,
    layer,
    showGrids,
    showParticles,
    setLayer,
    toggleGrids,
    toggleParticles,
    openValidation,
    openHindcastInfo,
    openSatelliteInfo,
  } = useAppStore();

  const canValidate = selectedHindcast != null && lon != null && lat != null;

  return (
    <Stack h="100%" gap="md" style={{ overflowY: "auto" }}>
      {/* Logo */}
      <Group justify="center" py="sm" pos="relative">
        <Anchor href="https://oceanum.io/" target="_blank">
          <Image
            src="/assets/oceanum-secondary-logo-marine-rgb-900px-w-72ppi.png"
            h={40}
            fit="contain"
            style={{ filter: "brightness(0) invert(1)", opacity: 0.9 }}
          />
        </Anchor>
        <CloseButton
          hiddenFrom="sm"
          onClick={onClose}
          pos="absolute"
          right={0}
          top="50%"
          style={{ transform: "translateY(-50%)" }}
        />
      </Group>

      {/* Title */}
      <Stack gap={6}>
        <Text fw={700} size="md" ta="center" ff="Poppins, sans-serif" c="white">
          Hindcast validation
        </Text>
        <Text size="sm" ta="center" lh={1.5} style={{ color: "#ace8f5" }}>
          Validate Oceanum wave hindcasts against the altimeter dataset of{" "}
          <Anchor
            href="https://www.nature.com/articles/s41597-019-0083-9"
            target="_blank"
            size="sm"
            c="oceanumPrimary.4"
          >
            Ribal and Young (2019)
          </Anchor>
          .
        </Text>
      </Stack>

      {/* Datasource */}
      <Stack gap={6}>
        <SectionLabel>Datasource</SectionLabel>
        <HindcastSelector />
      </Stack>

      {/* Variable + grid toggle inline */}
      <Stack id="tour-variable" gap={6}>
        <Group justify="space-between" align="center">
          <Tooltip label={MODE_TOOLTIP} position="right" multiline w={220}>
            <Box style={{ cursor: "help" }}>
              <SectionLabel>Variable</SectionLabel>
            </Box>
          </Tooltip>
          <Group gap="sm">
            {([
              { label: "Grids",     checked: showGrids,     onChange: toggleGrids,     tip: "Show hindcast domain boundaries on the map" },
              { label: "Particles", checked: showParticles, onChange: toggleParticles, tip: "Animate wave / wind direction with moving particles" },
            ] as const).map(({ label, checked, onChange, tip }) => (
              <Tooltip key={label} label={tip} position="bottom" withArrow>
                <Switch
                  label={label}
                  checked={checked}
                  onChange={onChange}
                  size="xs"
                  labelPosition="left"
                  styles={{ label: { fontSize: "var(--mantine-font-size-xs)", paddingRight: 4, color: "#ace8f5", cursor: "default" } }}
                />
              </Tooltip>
            ))}
          </Group>
        </Group>
        <SegmentedControl
          value={layer}
          onChange={(v) => setLayer(v as LayerType)}
          data={[
            { label: "Waves", value: "wave" },
            { label: "Wind", value: "wind" },
          ]}
          disabled={!selectedHindcast}
          fullWidth
          size="sm"
        />
      </Stack>

      {/* Location */}
      <Stack id="tour-location-section" gap={6}>
        <SectionLabel>Location</SectionLabel>
        <LocationInputs />
        {selectedHindcast && (
          <Text size="xs" c="dark.2" ta="center">
            or click on the map
          </Text>
        )}
      </Stack>

      {/* Search radius */}
      <Stack gap={6}>
        <SectionLabel>Search radius</SectionLabel>
        <RadiusSlider hideLabel />
      </Stack>

      {/* Validate */}
      <Box id="tour-validate" mt={4}>
        <Tooltip
          label={
            selectedHindcast == null
              ? "Select a datasource and location first"
              : "Select a location on the map or enter coordinates"
          }
          disabled={canValidate}
          position="right"
          multiline
          w={180}
        >
          <Button
            fullWidth
            size="md"
            disabled={!canValidate}
            onClick={openValidation}
            variant={canValidate ? "gradient" : "filled"}
            gradient={{ from: "oceanumPrimary.6", to: "oceanumPrimary.4", deg: 135 }}
          >
            {layer === "wind" ? "Validate winds" : "Validate waves"}
          </Button>
        </Tooltip>
      </Box>

      {/* Bottom section — pushed to foot of sidebar */}
      <Stack gap="md" mt="auto">
        <Divider opacity={0.2} />

        {/* Recent validations */}
        <RecentValidations />

        {/* About the data */}
        <Stack gap={6}>
          <SectionLabel>About the data</SectionLabel>
          <Group gap={6}>
            <Button
              variant="subtle"
              size="sm"
              c="oceanumPrimary.3"
              leftSection={<IconInfoCircle size={14} />}
              onClick={openSatelliteInfo}
              px="xs"
            >
              Altimeter
            </Button>
            <Tooltip
              label="Select a datasource first"
              disabled={selectedHindcast != null}
              position="right"
            >
              <Button
                variant="subtle"
                size="sm"
                c={selectedHindcast != null ? "oceanumPrimary.3" : "dark.3"}
                disabled={selectedHindcast == null}
                leftSection={<IconInfoCircle size={14} />}
                onClick={openHindcastInfo}
                px="xs"
              >
                Hindcast
              </Button>
            </Tooltip>
          </Group>
        </Stack>

        <Divider opacity={0.2} />

        {/* Contact */}
        <Text size="xs" ta="center" lh={1.5} pb={4} style={{ color: "#ace8f5" }}>
          <Anchor
            href="https://oceanum.io/contact/"
            target="_blank"
            size="xs"
            c="oceanumPrimary.4"
          >
            Contact us
          </Anchor>{" "}
          for data access options, spectra, custom domains and high resolution
          coastal hindcasts.
        </Text>
      </Stack>
    </Stack>
  );
}
